/* ZoneRenderer.js */

/* global Slick */
"use strict";
const _ = require("lodash");
const AddZoneComponent = require("./veil/addZone/AddZoneComponent");

/**
 * @type {{Control:{Action:{Button:ButtonControlComponent}}}}
 * @const
 */
const uiCore = require("@gluenetworks/steve_ui_core");

const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const CheckBoxEditor = require("./cellEditors/CheckBoxEditor");
const InputTextEditor = require("./cellEditors/InputTextEditor");
const formatters = require("./cellFormaters/ZoneCellFormatters");

const ButtonControl = uiCore.Control.Action.Button;

const NO_DATA_PLACEHOLDER_MSG = "No Engine Zones Assigned";

/**
 * class EnginesRenderer
 *
 * @listens EnginesComponent#event:DataUpdated
 *
 */
class ZoneRenderer {
    /**
     *
     * @param {EnginesComponent} model
     * @param {UiUtils} uiUtils
     */
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("zones-gl");
        this._rootElem.component = this._model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "Engine Zones";

        this._slickTableContainer = document.createElement("zones-table");

        this._engineZoneEnabledCheckBox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Manage Zones for this Organization",
            title: "Add, enable, and disable zones for the current organization",
            disabled: !this._model.isAuthorizedToManageZones(),
        });

        this._slickTable = undefined;
        this._moveRowsPlugin = undefined;

        /**
         * @type {ButtonControlComponent}
         * @private
         */
        this._cancelButton = new ButtonControl({label: "Cancel", darkTheme: true});
        this._saveButton = new ButtonControl({label: "Save", active: true, darkTheme: true});
        this._addZoneButton = new ButtonControl({
            label: "Add Zone +",
            active: true,
        });

        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._controlButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._saveButton.renderer.getRootElement());

        this._clickHandler = this._clickHandler.bind(this);
        this._cancelAction = this._cancelAction.bind(this);
        this._fitTableSize = this._fitTableSize.bind(this);

        this._onCellChangeAction = this._onCellChangeAction.bind(this);
        this._onRowCountChange = this._onRowCountChange.bind(this);
        this._onRowsChanged = this._onRowsChanged.bind(this);
        this._onZoneCreated = this._onZoneCreated.bind(this);
    }

    get inheritedKeys() {
        return this.model.getFieldsKeys("inherited") || [];
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    shutdown() {
        this._unregisterListeners();
        this._uiUtils.clearMessage();
        if (this._slickTable && this._slickTable.destroy) {
            this._slickTable.destroy();
        }
        this._rootElem.remove();
        this._stopStatePolling();

        return Promise.all([
            this._cancelButton.shutdown(),
            this._saveButton.shutdown(),
            this._engineZoneEnabledCheckBox.shutdown(),
            this._addZoneButton.shutdown(),
        ]).then(() => {
            this._model = null;
            this._slickTableContainer = null;
            this._moveRowsPlugin = null;
            this._tableOptions = null;
            this._columns = null;
            this._cancelButton = null;
            this._saveButton = null;
            this._engineZoneEnabledCheckBox = null;
            this._controlButtonsContainer = null;
            this._slickTable = null;

            this._rootElem = null;
        });
    }

    _registerListeners() {
        this._model.on("DataUpdated", (this._onDataUpdated = this._onDataUpdated.bind(this)));
        this._model.on("FeedReset", (this._onFeedReset = this._onFeedReset.bind(this)));
        this._cancelButton.on("UserClick", this._cancelAction);
        this._saveButton.on("UserClick", (this._saveAction = this._saveAction.bind(this)));
        this._addZoneButton.on("UserClick", (this._onAddZone = this._onAddZone.bind(this)));
        this._engineZoneEnabledCheckBox.on("UserValue", this._onOrgZonesEnabledChange.bind(this));
        window.addEventListener("resize", this._fitTableSize);
        document.addEventListener("click", this._clickHandler);
        if (this._slickTable) {
            this._slickTable.onCellChange.subscribe(this._onCellChangeAction);
            this._slickTable.onBeforeEditCell.subscribe(this._onBeforeEditCell.bind(this));
            this._slickDataView.onRowCountChanged.subscribe(this._onRowCountChange);
            this._slickDataView.onRowsChanged.subscribe(this._onRowsChanged);
        }
    }

    _unregisterListeners() {
        this._model.removeListener("DataUpdated", this._onDataUpdated);
        this._model.removeListener("OrgChanged", this._onFeedReset);
        this._cancelButton.removeListener("UserClick", this._cancelAction);
        this._saveButton.removeListener("UserClick", this._saveAction);
        this._addZoneButton.removeListener("UserClick", this._onAddZone);
        this._engineZoneEnabledCheckBox.removeAllListeners("UserValue");
        window.removeEventListener("resize", this._fitTableSize);
        document.removeEventListener("click", this._clickHandler);

        if (this._slickTable) {
            this._slickTable.onCellChange.unsubscribe(this._onCellChangeAction);
            this._slickTable.onBeforeEditCell.unsubscribe(this._onBeforeEditCell);
            this._slickDataView.onRowCountChanged.unsubscribe(this._onRowCountChange);
            this._slickDataView.onRowsChanged.unsubscribe(this._onRowsChanged);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    if (this._rootElem) {
                        this._rootElem.innerHTML = "";
                        this._rootElem.appendChild(this._headerElem);
                        this._rootElem.appendChild(this._createZoneSection());
                        this._rootElem.appendChild(this._controlButtonsContainer);
                        this._setActionButtonsAvailability(false);
                        this._toggleNoDataPlaceholder(_.isEmpty(this._getModelData()));

                        // BEGIN data table preparation section
                        this._slickDataView = new Slick.Data.DataView();
                        this._slickDataView.setItems(this._getModelData());

                        this._defineTable();
                        this._slickTable = new Slick.Grid(
                            this._slickTableContainer,
                            this._slickDataView,
                            this._columns,
                            this._tableOptions
                        );

                        this._slickTable.setSelectionModel(new Slick.RowSelectionModel());

                        this._markReadOnlyCells();
                        // END data table preparation section

                        this._slickTableContainer.appendChild(this._createNoDataPlaceholder());

                        this._registerListeners();
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    return this._checkOrgZonesManaged();
                })
                .then(() => {
                    this._startStatePolling();
                })
                .catch((err) => console.error(err) || Promise.reject(err));
        }
    }

    /******************************************/
    /* END PUBLIC COMPONENT RENDER METHODS    */
    /* START GETTERS AND SETTERS              */
    /******************************************/

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    getZoneCurrentState(engineId) {
        return this.model.engineZones[engineId]
            ? this.model.engineZones[engineId].currentState
            : "DELETED";
    }

    getZoneEngineCount(engineId) {
        return this.model.engineZones[engineId] ? this.model.engineZones[engineId].engineCount : 0;
    }

    getZoneEngineDisplayName(engineId) {
        return this.model.engineZones[engineId]
            ? this.model.engineZones[engineId].displayName
            : "UNKNOWN";
    }

    getEngineVersion(engineId) {
        return this.model.engineZones[engineId]
            ? this.model.engineZones[engineId].engineVersion
            : "";
    }

    /*******************************************/
    /* END GETTERS AND SETTERS                 */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _createZoneSection() {
        const _zoneSection = document.createDocumentFragment();

        _zoneSection.appendChild(
            this._uiUtils.wrapElement(
                this._engineZoneEnabledCheckBox.renderer.rootElement,
                "",
                "enable-zone"
            )
        );
        _zoneSection.appendChild(this._slickTableContainer);
        this._addZoneContainer = this._uiUtils.wrapElement(
            this._addZoneButton.renderer.getRootElement(),
            "",
            "add-new-zone"
        );
        if (this.getModel().isAuthorizedToManageZones()) {
            _zoneSection.appendChild(this._addZoneContainer);
        }

        return this._createSection(_zoneSection, {
            title: "Zones",
            className: "location-section",
            bordered: true,
        });
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _defineTable() {
        this._columns = [
            {
                id: "enabled",
                width: 20,
                name: "Enable",
                field: "enabled",
                toolTip: "Allow the zone to be used by this organization",
                formatter: formatters.checkboxFormatter,
                editor: CheckBoxEditor,
                headerCssClass: "centered",
            },
            {
                id: "default",
                width: 20,
                name: "Default",
                field: "default",
                toolTip: "Zone to run jobs on if devices are not assigned to a zone",
                formatter: formatters.checkboxFormatter,
                editor: CheckBoxEditor,
                headerCssClass: "centered",
            },
            {
                id: "allowForExtraCapacityUsage",
                width: 20,
                name: "Overflow Eligible",
                field: "allowForExtraCapacityUsage",
                toolTip:
                    "When enabled, this Zone will be assigned jobs for devices not locked to a specific Zone.",
                formatter: formatters.checkboxFormatter,
                editor: CheckBoxEditor,
                headerCssClass: "centered",
            },
            {
                id: "name",
                width: 60,
                name: "Display Name",
                field: "name",
                toolTip: "Name for the zone to appear in Device Explorer",
                formatter: formatters.displayNameFormatter,
                editor: InputTextEditor,
                cssClass: "cell-name",
            },
            {
                id: "engineZoneId",
                width: 60,
                name: "Zone",
                field: "engineZoneId",
                toolTip: "One-word internal name for the zone",
                formatter: formatters.engineFormatter,
                cssClass: "cell-zone",
            },
            {
                id: "currentState",
                width: 30,
                name: "Current State",
                field: "currentState",
                toolTip: "Operational state of the zone",
                formatter: formatters.currentState,
                cssClass: "cell-current-state",
            },
            {
                id: "engineCount",
                width: 40,
                name: "Engine Count",
                field: "engineCount",
                toolTip: "Number of active Gluware engines running in the zone",
                formatter: formatters.engineCountFormatter,
                cssClass: "cell-engine-count",
                headerCssClass: "centered",
            },
            {
                id: "engineVersion",
                width: 40,
                name: "Engine Version",
                field: "engineVersion",
                toolTip: "Version of the Gluware engines running in the zone",
                cssClass: "cell-engine-version centered",
                headerCssClass: "centered",
            },
            {
                id: "delete",
                width: 5,
                name: "Action",
                behavior: "deleteRow",
                selectable: false,
                toolTip: "Delete the zone",
                formatter: formatters.deleteButtonFormatter,
                asyncPostRender: this._deleteCellAddTooltips.bind(this),
                cssClass: "cell-delete centered",
                headerCssClass: "centered",
            },
        ];

        this._tableOptions = {
            editable: this.getModel().isManagingOrgZones(),
            enableCellNavigation: true,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: true,
            autoEdit: true,
            resizable: true,
            rowHeight: 45,
        };
    }

    _createNoDataPlaceholder() {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = NO_DATA_PLACEHOLDER_MSG;
        return noDataPlaceholder;
    }

    _toggleNoDataPlaceholder(state) {
        this._slickTableContainer.classList.toggle("empty", state);
    }

    /**
     * Commit and finish editing if click outside of the editor cell
     * @param {MouseEvent} e - click event
     * @private
     */
    _saveAndCloseEditCellAction(e) {
        //do nothing if no active editors
        if (!this._slickTable.getEditorLock().isActive()) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._slickTableContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._slickTable.getEditorLock().commitCurrentEdit();
        }
    }

    _deleteCellAddTooltips(cellNode /*, row, dataContext, colDef*/) {
        cellNode[0].setAttribute("title", "Delete Engine Location Field");
    }

    _setActionButtonsAvailability(state = null) {
        const currentData = this._slickDataView
            ? this._slickDataView.getItems()
            : this._getModelData();

        const isDisabled = state === null ? this._isDataNotBeenChanged(currentData) : !state;
        this._cancelButton.setDisabled(isDisabled);
        this._saveButton.setDisabled(isDisabled);
    }

    _fitTableSize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
        }
    }

    _showError(error) {
        if (_.get(error, "errorCode") !== "NoPendingUpdates") {
            this._uiUtils.showMessageDialog({
                message: "An error occurred: " + this._uiUtils.errorCodeMapping(error),
                confirmButtonName: "OK",
            });
        }
    }

    /*
     * This is a hack but it appears that we sometimes don't get updated when new zones are created.
     */
    _startStatePolling() {
        this._pollingInterval = setInterval(() => {
            let unAssignedZones = this._getUnassignedZones();
            if (
                this._saveButton.isDisabled() &&
                !this._slickTable.getEditorLock().isActive() &&
                !_.isEmpty(unAssignedZones)
            ) {
                this._resetData();
            }
        }, 30000);
    }

    _stopStatePolling() {
        clearInterval(this._pollingInterval);
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onCellChangeAction(e, args) {
        if (args) {
            const column = this._slickTable.getColumns()[args.cell];
            if (column && column.id === "default" && args.item && args.item.default) {
                this._enforceSingleDefault(args.item);
            }
        }
        this._setActionButtonsAvailability();
    }

    _enforceSingleDefault(selectedItem) {
        const activeOrgId = this.model.getActiveOrgId();
        this._slickDataView.getItems().forEach((item) => {
            if (item.id !== selectedItem.id && item.default && item.tenantId === activeOrgId) {
                item.default = false;
                this._slickDataView.updateItem(item.id, item);
            }
        });
        this._slickTable.invalidate();
        this._slickTable.render();
    }

    _onRowCountChange(event, {/*previous,*/ current}) {
        this._slickTable.invalidateAllRows();
        this._slickTable.resizeCanvas();
        this._toggleNoDataPlaceholder(!current);
    }

    _onRowsChanged() {
        this._setActionButtonsAvailability();
    }

    _onBeforeEditCell(e, args) {
        if (this._model.isAuthorizedToManageZones()) {
            if (args.column.id === "name" && args.item.inherited) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    _onDataUpdated() {
        if (!this._deletingZones) {
            this._resetData();
        }
    }

    _onFeedReset() {
        this._resetTableOptions();
        this._checkOrgZonesManaged();
    }

    _onOrgZonesEnabledChange(value) {
        if (value) {
            this._addZoneContainer.classList.remove("hidden");
            this._slickTableContainer.classList.remove("parent-zones");
            this.model.createOrgZonesToManage();
        } else {
            this._stopManagingOrgZones(value);
        }
        this.model.setManagingZones(value);
    }

    _onAddZone() {
        this._displayAddZone();
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete" &&
            target.dataset.uuid
        ) {
            this._deleteEngineZone(target.dataset.uuid);
        }
        this._saveAndCloseEditCellAction(e);
    }

    _cancelAction() {
        this._resetData();
    }

    _saveAction(e) {
        if (this._validateData()) {
            this._setActionButtonsAvailability(false);

            this._uiUtils.clearMessage();
            this._saveAndCloseEditCellAction(e);
            this._saveGrid();
        } else {
            this.model.userFeedback.displayError(this._errors);
        }
    }

    _validateData() {
        const data = this._slickDataView.getItems();
        let orgZones = false;
        let defaultCount = 0;
        let defaultEnabled = false;
        data.forEach((zoneData) => {
            orgZones = true;
            if (zoneData.default) {
                defaultCount++;
                if (zoneData.enabled) {
                    defaultEnabled = true;
                }
            }
        });
        if (orgZones && defaultCount !== 1) {
            if (defaultCount === 0) {
                this._errors = {
                    error: true,
                    errorCode: "MissingDefaultZone",
                    errorMsg: "One zone at this level must be marked as default.<br><br>",
                    title: "No Specified Default Zone",
                };
            } else {
                this._errors = {
                    error: true,
                    errorCode: "TooManyDefaultZones",
                    errorMsg: "Only one zone can be specified as default at this level.<br><br>",
                    title: "Too Many Default Zones",
                };
            }
            return false;
        } else {
            if (!defaultEnabled) {
                this._errors = {
                    error: true,
                    errorCode: "DefaultNotEnabled",
                    errorMsg: "The default zone must be enabled.<br><br>",
                    title: "Default Must Be Enabled",
                };
                return false;
            } else {
                return true;
            }
        }
    }

    _saveGrid() {
        const data = this._slickDataView.getItems();
        const convertedData = this._convertSlickTableFormatToZonesData(data);
        return this.model
            .updateOrgSpecificZoneData(convertedData)
            .then(() => this._resetData())
            .catch((error) => {
                // console.error({ error });
                if (_.isArray(error)) {
                    error.map((errorMsg) => this._uiUtils.writeErrorMessage(errorMsg));
                } else if (_.has(error, "errorCode")) {
                    this._resetData();
                    this._showError(error);
                } else {
                    console.error(error);
                }
                this._cancelButton.setDisabled(false);
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getUnassignedZones() {
        let unassignedZones = _.cloneDeep(this.model.getEngineZones());
        let currentList = this._slickDataView.getItems();
        currentList.forEach((item) => {
            delete unassignedZones[item.engineZoneId];
        });
        return unassignedZones;
    }

    /**
     * This function will determine if there are orgZones at this org level.
     * If there are, it will turn on the checkbox flag and attempt to load any
     * engines that are currently not associated with this org.
     * @returns {Promise<void>}
     * @private
     */
    async _checkOrgZonesManaged() {
        // If there are orgZones assigned for this org then check we have all
        // available engines displaying
        if (this.getModel().isManagingOrgZones()) {
            this._engineZoneEnabledCheckBox.setValue(true);
            this._addZoneContainer.classList.remove("hidden");
            this._slickTableContainer.classList.remove("parent-zones");
        } else {
            this._engineZoneEnabledCheckBox.setValue(false);
            this._addZoneContainer.classList.add("hidden");
            this._slickTableContainer.classList.add("parent-zones");
        }
    }

    /**
     * Map function to convert zone item data to SlickGrid item data
     * @param zoneData
     * @returns {function(Object=): Object}
     * @private
     */
    _convertZonesDataToSlickTableFormat(zoneData) {
        let tableData = [];
        this._inheritedRowCount = 0;
        for (let zoneKey in zoneData) {
            let orgZone = zoneData[zoneKey];
            tableData.push({
                id: zoneKey,
                enabled: !!orgZone.zoneEnabled,
                name: this.getZoneEngineDisplayName(orgZone.engineZoneId),
                zoneEnabled: orgZone.zoneEnabled,
                engineZoneId: orgZone.engineZoneId,
                currentState: this.getZoneCurrentState(orgZone.engineZoneId),
                engineCount: this.getZoneEngineCount(orgZone.engineZoneId),
                engineVersion: this.getEngineVersion(orgZone.engineZoneId),
                inherited: this.model.isInherited(orgZone.engineZoneId),
                tenantId: orgZone.tenantId,
                default: !!orgZone.defaultZone,
                allowForExtraCapacityUsage: !!orgZone.allowForExtraCapacityUsage,
                authorized: this.model.isAuthorizedToManageZones(),
                renderer: this,
            });
            if (this.model.isInherited(orgZone.engineZoneId)) {
                this._inheritedRowCount++;
            }
        }

        tableData.sort((a, b) => {
            if (a.inherited && !b.inherited) {
                return -1;
            } else if (!a.inherited && b.inherited) {
                return 1;
            } else {
                return a.name.localeCompare(b.name);
            }
        });
        return tableData;
    }

    /**
     * Convert SlickGrid data to proper format before saving
     * @param {Array<Object>} slickGridData - pure SlickGrid data
     * @returns {Object<string, EngineData>} - converted data object
     */
    _convertSlickTableFormatToZonesData(slickGridData) {
        return slickGridData.reduce((accumulator, zone) => {
            const fieldId = zone.id;
            accumulator[fieldId] = {
                id: zone.id,
                name: zone.name,
                engineZoneId: zone.engineZoneId,
                enabled: !!zone.enabled,
                defaultZone: !!zone.default,
                allowForExtraCapacityUsage: !!zone.allowForExtraCapacityUsage,
                inherited: zone.inherited,
            };
            return accumulator;
        }, {});
    }

    _resetData() {
        const data = this._getModelData();
        this._slickDataView.setItems(data);
        this._slickTable.invalidate();
        this._markReadOnlyCells();

        this._setActionButtonsAvailability(false);
    }

    _resetTableOptions() {
        this._defineTable();
        this._slickTable.setOptions(this._tableOptions);
    }

    _isDataNotBeenChanged(currentData) {
        return _.isEqual(this._getModelData(), currentData);
    }

    _getModelData() {
        const orgZones = this.model.orgZones;
        return this._convertZonesDataToSlickTableFormat(orgZones);
    }

    _stopManagingOrgZones(value) {
        const stopManagingConfirmation = () => {
            let deleteMessage = "";
            if (this.model.zoneExistsAtOrgLevel()) {
                deleteMessage =
                    "WARNING - Zones have been created at this organization level! Stopping management " +
                    "at this level will result in these zones being hidden from this organization, since the parent " +
                    "organization has no visibility to these zones. <br><br>";
            }
            deleteMessage =
                deleteMessage +
                `Are you sure you want to turn off zone engines at this organization level? 
            Zone management will now be inherited from parent organization.`;

            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
                enableHtml: true,
            });
        };

        return stopManagingConfirmation()
            .then(
                async () => {
                    this._addZoneContainer.classList.add("hidden");
                    this._slickTableContainer.classList.add("parent-zones");
                    await this.model.clearOrgZonesForOrg();
                },
                () => this._engineZoneEnabledCheckBox.setValue(!value)
            )
            .catch((error) => {
                error && this._showError(error);
            })
            .finally(() => {
                setTimeout(() => {
                    this._deletingZones = false;
                }, 100);
            });
    }

    _deleteEngineZone(orgZoneId) {
        let deleteItem = this._slickDataView.getItemById(orgZoneId);
        if (deleteItem.currentState === "ACTIVE") {
            const stopDeleteMessage = `This zone cannot be deleted in its current state <br /> <br />
                    An ACTIVE zone cannot be deleted.`;

            this._uiUtils.showMessageDialog({
                message: stopDeleteMessage,
                enableHtml: true,
            });
        } else {
            const deleteZoneConfirmation = () => {
                const deleteMessage = `Are you sure you want to delete this zone? <br /><br />
            Note: Even if a zone is deleted, if an engine exists on the system for this zone and the engine
            is restarted, the zone will automatically be created again.`;

                return this._uiUtils.showDeleteConfirmDialog({
                    message: deleteMessage,
                    confirmButtonName: "Confirm",
                    cancelButtonName: "Cancel",
                    enableHtml: true,
                });
            };

            return deleteZoneConfirmation()
                .then(
                    async () => {
                        let orgZoneEntity = this.model.orgZones[orgZoneId];
                        this.model.deleteEngineZone(orgZoneEntity.engineZoneId);
                    },
                    () => {
                        // delete canceled
                    }
                )
                .catch((error) => {
                    error && this._showError(error);
                });
        }
    }

    _onZoneCreated() {
        setTimeout(() => {
            this.addZoneComponent.removeAllListeners("ZoneCreated");
            this._resetData();
        }, 100);
    }

    _markReadOnlyCells() {
        if (this.model.isAuthorizedToManageZones()) {
            let inheritedCells = {};
            for (let idx = 0; idx < this._inheritedRowCount; idx++) {
                inheritedCells[idx] = {
                    name: "read-only",
                    delete: "read-only",
                };
            }
            if (!_.isEmpty(inheritedCells)) {
                this._slickTable.setCellCssStyles("readOnly", inheritedCells);
            } else {
                this._slickTable.removeCellCssStyles("readOnly");
            }
        } else {
            let readOnlyCells = {};
            for (let idx = 0; idx < this._slickDataView.getLength(); idx++) {
                readOnlyCells[idx] = {
                    name: "read-only",
                    delete: "read-only",
                    enabled: "read-only",
                    default: "read-only",
                    allowForExtraCapacityUsage: "read-only",
                };
            }
            if (!_.isEmpty(readOnlyCells)) {
                this._slickTable.setCellCssStyles("readOnly", readOnlyCells);
            } else {
                this._slickTable.removeCellCssStyles("readOnly");
            }
        }
    }

    async _displayAddZone() {
        this.addZoneComponent = new AddZoneComponent({
            engineZoneEntity: this._model.getEngineZoneEntity(),
            engineZoneEntityFeed: this._model.getEngineZoneEntityFeed(),
            orgId: this._model.getActiveOrgId(),
            orgHasLicense: await this._model.orgHasLicense(),
        });
        this.addZoneComponent.on("ZoneCreated", this._onZoneCreated);
        this.model.userFeedback
            .displayCustom(this.addZoneComponent, {notDismissable: false})
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.error("Error in ZoneEditor", null, error);
                }
            });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ZoneRenderer;
